import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { Description, FaqAccordion, type FaqItem, Title, ViewAllLink } from './components';
import { FaqPageJsonLd } from './FaqPageJsonLd/FaqPageJsonLd';

type FaqSectionProps = ComponentProps<'div'> & {
  namespace: string;
  viewAllLink?: string;
};

/**
 * A section component that displays a list of frequently asked questions in an accordion format.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.namespace - The translation path prefix for accessing FAQ-related translations
 * @param {string} props.viewAllLink - The URL for the "View All" link
 * @returns {JSX.Element} A section containing FAQ items in an accordion format with a title and optional description
 */
function FaqSection({ namespace, viewAllLink, className, ...props }: FaqSectionProps) {
  const t = useTranslations(namespace);
  const faqItems = (t.has('faq_items') ? t.raw('faq_items') : []) as FaqItem[];

  return (
    <div className={twMerge('flex flex-col', className)} data-testid="faq-section" {...props}>
      <FaqPageJsonLd items={faqItems} />
      <div className="flex justify-between gap-4">
        <Title>{t('title')}</Title>
        {viewAllLink ? <ViewAllLink href={viewAllLink}>{t('view_all')}</ViewAllLink> : null}
      </div>
      {t.has('description') ? <Description text={t.raw('description')} className="mb-5" /> : null}
      {t.has('additional_title') ? <Title>{t('additional_title')}</Title> : null}
      <FaqAccordion items={faqItems} />
      {t.has('bottom_title') ? <Title className="mt-5">{t('bottom_title')}</Title> : null}
      {t.has('bottom_description') ? <Description text={t.raw('bottom_description')} /> : null}
    </div>
  );
}

export { FaqSection, type FaqSectionProps };
